import axios from 'axios';
import { Config } from 'config';
import {
    IAlternateContact,
    IByod,
    ICartProps,
    ICustomerAgreements,
    ILineItem,
    IPhoneNumberInformation,
    IShippingMethod
} from 'contexts/CartContext/CartContext.types';
import { ICreateCartResponse } from '../../api/Idi/Idi.api.types';
import { SendEmailByTemplate } from 'api/Email/Email.api';

const Url = `${Config.url.CART_URL}/api/v1/Cart`;
const fileUrl = `${Config.url.CART_URL}/api/v1/File`;
const serviceAgreementFile = `/data/viaero-service-agreement.pdf`;

interface ICartRequest {
    id: string;
    customerInfo: unknown;
    alternateContact: IAlternateContact;
    agreements: ICustomerAgreements;
    lineItems: ISaveCartLineItem[];
    accessories: string[];
    shippingMethod: IShippingMethod;
    autopay: boolean;
    eBill: boolean;
    closestStoreTimeZone: string;
    idiCart?: ICreateCartResponse;
    rawCartJson: string;
}

interface ISaveCartLineItem {
    lineNumber: number;
    isPrimaryLine: boolean;
    phoneNumberInformation: IPhoneNumberInformation;
    deviceId: string | null;
    deviceSkus: string[];
    promoSku: string;
    planId: string;
    planSkus: string[];
    planDiscountSku: string;
    byod?: IByod;
    assurance: boolean;
    tradeInValue?: number;
    downPayment?: number;
}

interface ICartFileRequest {
    file: File;
    name: string;
}

export const SaveCart = (id: string, cart: ICartProps, onSuccess: () => void) => {
    const lineItems: ISaveCartLineItem[] =
        cart.lineItems
            .filter((x) => (x.device?.product.id || x.byod?.imei) && x.planSelection?.plan.id)
            .map<ISaveCartLineItem>((x) => {
                if (x.byod) x.byod.fileName = GetByodFilename(x, id);

                const deviceSkus = x.device?.product.baseSkus.map((d) => d.sku) ?? [];
                if (x.device?.productSku?.sku.sku) deviceSkus.push(x.device?.productSku?.sku.sku);

                const planSkus = x.planSelection?.plan.baseSkus.map((d) => d.sku) ?? [];
                if (x.planSelection?.planSku.sku) planSkus.push(x.planSelection?.planSku.sku.sku);

                return {
                    lineNumber: x.lineNumber,
                    isPrimaryLine: x.isPrimaryLine,
                    phoneNumberInformation: x.phoneNumberInformation,
                    deviceId: x.device?.product.id ?? null,
                    deviceSkus: deviceSkus,
                    promoSku: x.promoSummary?.promoResponse.promo.sku ?? '',
                    planId: x.planSelection?.plan.id ?? '',
                    planSkus: planSkus,
                    planDiscountSku: x.planSelection?.customerAppreciationDiscount?.sku ?? '',
                    byod: x.byod,
                    assurance: x.device?.useAssurance ?? false,
                    tradeInValue: x.device?.tradeIn?.price,
                    downPayment: x.device?.downPayment
                };
            }) ?? [];

    const accessorySkuList: string[] = [];

    cart.accessories.forEach((a) => {
        for (let x = 0; x < a.quantity; x++) {
            accessorySkuList.push(a.productSku?.sku.sku ?? '');
        }
    });

    const request: ICartRequest = {
        id: id,
        customerInfo: {
            firstName: cart.customerInfo.firstName,
            lastName: cart.customerInfo.lastName,
            address: cart.customerInfo.address,
            phoneNumber: cart.customerInfo.phoneNumber,
            emailAddress: cart.customerInfo.emailAddress,
            dob: new Date(cart.customerInfo.dob),
            customerId: cart.customerInfo.customerId,
            employer: cart.customerInfo.employer
        },
        alternateContact: cart.alternateContact,
        agreements: cart.agreements,
        lineItems: lineItems,
        accessories: accessorySkuList,
        shippingMethod: cart.shippingMethod,
        autopay: cart.autopay,
        closestStoreTimeZone: cart.closestStoreTimeZone ?? '',
        idiCart: cart.idiCart,
        rawCartJson: JSON.stringify(cart),
        eBill: cart.eBill
    };

    if (request.lineItems.length < 1) return;

    axios.post<ICartRequest>(Url, request).then(onSuccess, (e) => console.log(e));
};

export const SaveFile = (request: ICartFileRequest) => {
    axios
        .post<ICartRequest>(fileUrl, request, {
            headers: { 'Content-Type': `multipart/form-data; ${request.file.type}` }
        })
        .then(
            (e) => console.log(e),
            (e) => console.log(e)
        );
};

export const SendPurchaseSuccessEmail = (cart: ICartProps) => {
    let monthlyTotal = 0;
    cart.monthlySummary.forEach((item) => {
        if (item.type == 'cost') {
            monthlyTotal += item.amount;
            return;
        }
        monthlyTotal -= item.amount;
    });

    let todayTotal = 0;
    cart.dueTodaySummary.forEach((item) => {
        if (item.type == 'cost') {
            todayTotal += item.amount;
            return;
        }
        todayTotal -= item.amount;
    });

    SendEmailByTemplate(Config.purchaseEmail, 'Purchase', 'A Purchase Has Been Made', {
        cart: cart,
        monthlyTotal: monthlyTotal,
        todayTotal: todayTotal,
        contractUrls: cart.agreements.contractUrls,
        serviceAgreementUrl: `${Config.url.STORAGE_URL}${serviceAgreementFile}`
    });
};

export const SendInternetOrderEmail = (cart: ICartProps) => {
    SendEmailByTemplate(
        Config.internetOrderEmail,
        'InternetServiceInquiry',
        'Internet Service Inquiry',
        {
            cart: cart
        }
    );
};

export const SendCreditCheckSuccessEmail = (cart: ICartProps) => {
    SendEmailByTemplate(
        Config.creditCheckSuccessEmail,
        'CreditCheckSuccess',
        'A credit check has passed',
        {
            cart: cart
        }
    );
};

export const SendCreditCheckFailureEmail = (cart: ICartProps) => {
    SendEmailByTemplate(
        Config.creditCheckFailureEmail,
        'CreditCheckFailure',
        'A credit check failure has occurred',
        {
            cart: cart
        }
    );
};

export const SendCreditCheckAssistanceRequestEmail = (cart: ICartProps) => {
    SendEmailByTemplate(
        Config.creditCheckAssistanceRequestEmail,
        'CreditCheckAssistanceRequest',
        'Credit check assistance requested',
        {
            cart: cart
        }
    );
};

export const GetByodFilename = (lineItem: ILineItem, id: string): string => {
    return `byod_${lineItem.lineNumber}_${id}.${lineItem.byod?.file?.name.split('.')[1]}`;
};
